import { gql } from '@apollo/client';

export const getBusinessPartnersList = gql`
  query SearchBusinessPartners(
    $keyword: String
    $pagination: PaginationInput
    $orderBy: OrderByInput
    $companies: [String!]
    $types: [String!]
    $statuses: [String!]
    $isLocal: Boolean
    $isCompany: Boolean
    $isRegistered: Boolean
    $createdOnFrom: DateTime
    $createdOnTo: DateTime
  ) {
    salesforce {
      searchBusinessPartners(
        keyword: $keyword
        pagination: $pagination
        orderBy: $orderBy
        companies: $companies
        types: $types
        statuses: $statuses
        isLocal: $isLocal
        isCompany: $isCompany
        isRegistered: $isRegistered
        createdOnFrom: $createdOnFrom
        createdOnTo: $createdOnTo
      ) {
        paging {
          pageNumber
          pageSize
          totalCount
        }
        items {
          id
          code
          fullName
          status
          type
          relatedCompany
          isLocal
          isCompany
          isRegistered
          createdOn
          modifiedOn
          hasAccessToAllInsured
          person {
            fullName
            email
            id
            phoneDetails {
              phoneNumber
              phoneType
            }
          }
        }
      }
    }
  }
`;

export const getBusinessPartnerFilters = gql`
  query {
    salesforce {
      getRelatedCompanies {
        id
        title
      }
    }
    BusinessPartnerStatuses: __type(name: "BusinessPartnerStatus") {
      enumValues {
        name
      }
    }
    BusinessPartnerTypes: __type(name: "BusinessPartnerType") {
      enumValues {
        name
      }
    }
  }
`;

export const getBusinessPartnerDetails = gql`
  query getBusinessPartnerDetailsInfo($id: String!) {
    salesforce {
      getBusinessPartnerDetails(id: $id) {
        auxiliaryAccount
        code
        createdBy
        createdOn
        fullName
        hasAccessToAllInsured
        id
        isCompany
        isLocal
        isRegistered
        maxCreditLimitAllowed
        maxCreditLimitAllowedCurrency
        modifiedBy
        modifiedOn
        personId
        registerationExpiryDate
        registerationNumber
        relatedCompany
        status
        taxAuxiliaryAccount
        type

        company {
          code
          createdOn
          externalCode
          id
          modifiedOn
          title
        }

        person {
          id
          type
          title
          firstName
          middleName
          lastName
          fullName
          email
          code
          dateOfBirth
          isTaxable
          phoneDetails {
            phoneNumber
            phoneType
          }
        }

        currency {
          id
          code
          title
          titleAr
          symbol
        }
      }
    }
  }
`;

export const getBusinessPartnerLinesList = gql`
  query getBusinessPartnerLinesList(
    $keyword: String
    $pagination: PaginationInput
    $orderBy: OrderByInput
    $businessPartnerId: String
  ) {
    salesforce {
      searchBusinessPartnerLines(
        keyword: $keyword
        pagination: $pagination
        orderBy: $orderBy
        businessPartnerId: $businessPartnerId
      ) {
        items {
          businessPartnerID
          commissionAmount
          commissionAmountCurrency
          commissionApplicableOn
          commissionRate
          commissionType
          createdBy
          createdOn
          creditLimit
          creditLimitCurrency
          creditLimitReached
          gracePeriod
          id
          lineID
          modifiedBy
          modifiedOn
          status
          commissionAmountCurrencyValue {
            code
            createdOn
            id
            modifiedOn
            status
            symbol
            title
            titleAr
          }
          creditLimitCurrencyValue {
            code
            createdOn
            id
            modifiedOn
            status
            symbol
            title
            titleAr
          }
          commissionApplicableOnValue {
            code
            createdOn
            id
            modifiedOn
            title
          }
          line {
            abbreviation
            abbreviationArabic
            code
            createdBy
            createdOn
            id
            modifiedBy
            modifiedOn
            name
            nameArabic
            status
          }
        }
        paging {
          pageNumber
          pageSize
          totalCount
        }
      }
    }
  }
`;

export const getBusinessPartnerPlanList = gql`
  query searchBusinessPartnerPlans(
    $businessPartnerId: String!
    $keyword: String
    $pagination: PaginationInput
    $orderBy: OrderByInput
  ) {
    salesforce {
      searchBusinessPartnerPlans(
        businessPartnerId: $businessPartnerId
        keyword: $keyword
        pagination: $pagination
        orderBy: $orderBy
      ) {
        items {
          id
          planID
          lineID
          businessPartnerID
          businessPartnerLineID
          commissionType
          commissionAmount
          commissionRate
          commissionApplicableOn
          status
          activeFrom
          activeTo
          line {
            code
            name
            status
          }
          plan {
            code
            name
            status
          }
          commissionAmountCurrencyValue {
            symbol
            code
            title
          }
          commissionApplicableOnValue {
            code
            title
          }
          businessPartnerLine {
            status
          }
          businessPartner {
            status
          }
        }
        paging {
          pageNumber
          pageSize
          totalCount
        }
      }
    }
  }
`;

export const getBusinessPartnerVignetteRangeList = gql`
  query getBusinessPartnerVignetteRangeList(
    $pagination: PaginationInput!
    $businessPartnerId: String!
    $OrderBy: OrderByInput!
    $keywordSearch: String
  ) {
    salesforce {
      searchBusinessPartnerVignetteRanges(
        pagination: $pagination
        businessPartnerId: $businessPartnerId
        orderBy: $OrderBy
        keyword: $keywordSearch
      ) {
        paging {
          pageSize
          pageNumber
          totalCount
        }
        items {
          businessPartnerID
          createdBy
          createdOn
          id
          modifiedBy
          modifiedOn
          rangeFrom
          rangeTo
          status
          vignetteCode
          vignetteYear
          businessPartner {
            auxiliaryAccount
            code
            createdBy
            createdOn
            fullName
            hasAccessToAllInsured
            id
            isCompany
            isLocal
            isRegistered
            maxCreditLimitAllowed
            maxCreditLimitAllowedCurrency
            modifiedBy
            modifiedOn
            personId
            registerationExpiryDate
            registerationNumber
            relatedCompany
            status
            taxAuxiliaryAccount
            totalCreditLimitReached
            type
          }
        }
      }
    }
  }
`;

export const getBusinessPartnerPayments = gql`
  query getBusinessPartnerPayments(
    $businessPartnerId: String!
    $keyword: String
    $pagination: PaginationInput!
    $OrderBy: OrderByInput!
    $selectedCurrencies: [String!]!
    $selectedLines: [String!]!
    $policyIssuanceFrom: DateTime
    $policyIssuanceTo: DateTime
  ) {
    accounting {
      getBrokerPolicyDuesList(
        businessPartnerID: $businessPartnerId
        keyword: $keyword
        pagination: $pagination
        orderBy: $OrderBy
        selectedCurrencies: $selectedCurrencies
        selectedLines: $selectedLines
        policyIssuanceFrom: $policyIssuanceFrom
        policyIssuanceTo: $policyIssuanceTo
      ) {
        items {
          referenceNumber
          items {
            amountDue
            amountDueBasic
            amountDueSecondary
            amountPaid
            amountPaidBasic
            amountPaidSecondary
            businessPartnerID
            createdBy
            createdOn
            currencyID
            dueDate
            id
            issueDate
            linkedPolicyDueID
            modifiedBy
            modifiedOn
            number
            outstandingBalance
            outstandingBalanceBasic
            outstandingBalanceSecondary
            periodFrom
            periodTo
            policyBPPaymentID
            policyID
            policyPaymentID
            relatedCompanyID
            status
            taxOnCommission
            taxOnCommissionBasic
            taxOnCommissionSecondary
            totalCommission
            totalCommissionBasic
            totalCommissionSecondary
            type
            currency {
              code
              createdOn
              id
              modifiedOn
              status
              symbol
              title
              titleAr
            }
            policyPayment {
              basicPremium
              basicPremiumBasic
              basicPremiumSecondary
              basicToPolicyRate
              chargesAmount
              chargesAmountBasic
              chargesAmountBeforeRounding
              chargesAmountSecondary
              chargesPercentage
              createdBy
              createdOn
              fixedStampAmount
              fixedStampAmountBasic
              fixedStampAmountSecondary
              frequencyID
              grossPremium
              grossPremiumBasic
              grossPremiumBeforeRounding
              grossPremiumSecondary
              id
              issueDate
              modifiedBy
              modifiedOn
              municipalityTaxAmount
              municipalityTaxAmountBasic
              municipalityTaxAmountSecondary
              municipalityTaxBeforeRounding
              netPremium
              netPremiumBasic
              netPremiumSecondary
              nextIssueDate
              number
              periodFrom
              periodTo
              planToBasicRate
              planToPolicyRate
              policyCost
              policyCostBasic
              policyCostSecondary
              policyCurrencyID
              policyID
              policyQuoteID
              policyToSecondaryRate
              proportionalStampAmount
              proportionalStampAmountBasic
              proportionalStampAmountSecondary
              proportionalStampBeforeRounding
              referenceNumber
              reinsuranceTax
              reinsuranceTaxAmount
              roundingDifference
              taxOnCommissionAmount
              taxOnCommissionAmountBasic
              taxOnCommissionAmountSecondary
              totalCommission
              totalCommissionBalance
              totalCommissionBasic
              totalCommissionPaid
              totalCommissionSecondary
              totalPremium
              totalPremiumArabic
              totalPremiumArabicLetters
              totalPremiumBalance
              totalPremiumBasic
              totalPremiumBeforeRounding
              totalPremiumLetters
              totalPremiumReceived
              totalPremiumSecondary
              tpaFeesTotalAmount
              tpaFeesTotalAmountBasic
              tpaFeesTotalAmountSecondary
            }
          }
        }
        paging {
          pageNumber
          pageSize
          totalCount
        }
      }
    }
  }
`;
